import React, { Fragment } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";
import "./InsightsDetails.css";

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const articles: Record<string, {
  category: string;
  kicker: string;
  title: string;
  intro: string;
  readTime: string;
  date: string;
  sections: { title: string; paragraphs: string[] }[];
}> = {
  "designing-for-reliability-advanced-semiconductor-nodes": {
    category: "Silicon Design & Architecture",
    kicker: "Engineering Insight",
    title: "Designing for Reliability in Advanced Semiconductor Nodes",
    intro:
      "Reliability at advanced process nodes is no longer a late-stage sign-off concern. It is an engineering discipline that connects architecture, implementation, verification, power intent, timing, physical effects, and product operating conditions.",
    readTime: "5 min read",
    date: "Engineering Perspective",
    sections: [
      {
        title: "Reliability begins with engineering intent",
        paragraphs: [
          "As geometries shrink and design density increases, margins become more sensitive to variation, electrical stress, thermal behavior, and implementation choices. Reliable silicon therefore begins with clear design intent and a shared understanding of the operating conditions the product must sustain.",
          "Engineering teams benefit when reliability requirements are translated into decisions early enough to influence architecture, power distribution, clocking, placement, routing, and verification planning rather than being treated only as downstream checks."
        ]
      },
      {
        title: "Implementation and verification must reinforce each other",
        paragraphs: [
          "Physical implementation introduces constraints that can affect real device behavior, while verification provides the evidence that architectural and implementation assumptions remain valid. Strong programs connect these disciplines through measurable criteria, focused reviews, and feedback loops.",
          "The objective is not simply to pass more checks. It is to identify the failure modes that matter, understand their causes, and close the gap between intended behavior and manufactured reality."
        ]
      },
      {
        title: "Engineering discipline creates confidence",
        paragraphs: [
          "Advanced-node reliability is ultimately built through disciplined execution: clear ownership, early risk identification, repeatable methodology, effective automation, and technically informed decisions. When these practices are embedded throughout the lifecycle, teams can improve confidence while reducing late-stage surprises."
        ]
      }
    ]
  },

  "why-verification-strategy-matters": {
    category: "Verification & Functional Validation",
    kicker: "Technical Perspective",
    title: "Why Verification Strategy Matters More Than Verification Volume",
    intro:
      "More tests do not automatically create more confidence. Verification effectiveness depends on whether the strategy is aligned with architecture, risk, coverage intent, debug efficiency, and the behaviors that matter most to the product.",
    readTime: "6 min read",
    date: "Technical Viewpoint",
    sections: [
      {
        title: "Verification should be driven by risk",
        paragraphs: [
          "A strong verification plan begins by understanding what can fail, where complexity is concentrated, and which behaviors carry the greatest system impact. This helps teams focus effort where additional evidence provides the most value.",
          "Test volume becomes meaningful only when it maps back to clear verification objectives, measurable coverage, and known risk areas."
        ]
      },
      {
        title: "Coverage is a decision tool",
        paragraphs: [
          "Coverage is most useful when it helps engineers reason about what has and has not been exercised. Functional coverage, code coverage, assertions, scenario tracking, and review evidence work best as complementary signals rather than isolated targets.",
          "A strategic approach also improves debug because failures can be interpreted in the context of intended verification goals."
        ]
      },
      {
        title: "Quality comes from focus and feedback",
        paragraphs: [
          "Effective verification organizations continuously refine their plans as architecture matures, bugs reveal new risk, and coverage data exposes blind spots. This feedback loop is more valuable than simply increasing regression size without changing what the team learns."
        ]
      }
    ]
  },

  "growing-role-ai-semiconductor-development": {
    category: "Emerging Technologies",
    kicker: "Industry Viewpoint",
    title: "The Growing Role of AI in Semiconductor Product Development",
    intro:
      "AI is increasingly influencing semiconductor engineering—not as a replacement for engineering judgment, but as a new layer of assistance across architecture exploration, verification, implementation, validation, and engineering productivity.",
    readTime: "5 min read",
    date: "Industry Perspective",
    sections: [
      {
        title: "AI can amplify engineering productivity",
        paragraphs: [
          "Semiconductor programs generate large volumes of structured and unstructured engineering information. AI-assisted workflows can help engineers navigate logs, classify failures, summarize trends, generate starting points, and prioritize areas that deserve deeper investigation.",
          "The highest-value use cases are often those that reduce repetitive effort while keeping technical decisions in the hands of experienced engineers."
        ]
      },
      {
        title: "Better context creates better outcomes",
        paragraphs: [
          "AI systems are most useful when they operate with the right engineering context: design intent, methodology, tool outputs, historical decisions, verification status, and project constraints. Without that context, speed alone can create noise rather than insight."
        ]
      },
      {
        title: "The opportunity is improved decision-making",
        paragraphs: [
          "The long-term opportunity is not simply to automate individual tasks. It is to help engineering teams connect information earlier, identify patterns faster, and make more informed decisions across the semiconductor product development lifecycle."
        ]
      }
    ]
  }
};

const fallback = articles["designing-for-reliability-advanced-semiconductor-nodes"];

const InsightsDetails: React.FC = () => {
  const { slug } = useParams();
  const article = (slug && articles[slug]) || fallback;

  return (
    <Fragment>
      <div className="insight-detail-page inner-page">
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <section className="id-hero">
              <div className="id-hero__grid" />
              <div className="id-hero__glow" />

              <div className="id-shell">
                <Link className="id-back" to="/insights">
                  ← Back to Engineering Perspectives
                </Link>

                <div className="id-hero__layout">
                  <div className="id-hero__copy">
                    <span className="id-kicker">{article.kicker}</span>
                    <span className="id-category">{article.category}</span>
                    <h1>{article.title}</h1>
                    <p>{article.intro}</p>

                    <div className="id-meta">
                      <span>{article.readTime}</span>
                      <span>{article.date}</span>
                    </div>
                  </div>

                  <div className="id-hero__visual" aria-hidden="true">
                    <div className="id-silicon">
                      <i /><i /><i /><i />
                      <div className="id-silicon__core">
                        <span>ENGINEERING</span>
                        <strong>INSIGHT</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="id-body">
              <div className="id-shell id-body__layout">
                <aside className="id-aside">
                  <span>IN THIS PERSPECTIVE</span>
                  {article.sections.map((section, index) => (
                    <a href={`#section-${index + 1}`} key={section.title}>
                      0{index + 1} {section.title}
                    </a>
                  ))}
                </aside>

                <article className="id-article">
                  {article.sections.map((section, index) => (
                    <section id={`section-${index + 1}`} className="id-article__section" key={section.title}>
                      <span className="id-section-no">0{index + 1}</span>
                      <h2>{section.title}</h2>
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </section>
                  ))}

                  <div className="id-takeaway">
                    <span>ENGINEERING TAKEAWAY</span>
                    <p>
                      Better semiconductor outcomes come from combining practical experience,
                      disciplined methodology, and informed engineering decisions throughout
                      the product development lifecycle.
                    </p>
                  </div>
                </article>
              </div>
            </section>

            <section className="id-next">
              <div className="id-shell id-next__inner">
                <div>
                  <span>CONTINUE EXPLORING</span>
                  <h2>More Engineering Perspectives</h2>
                </div>
                <Link to="/insights" className="id-button">
                  View All Perspectives <Arrow />
                </Link>
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default InsightsDetails;
